/**
 * Dailies — the day-to-day task list.
 *
 * Two views: what is still pending, and a log of what was completed grouped by
 * day. A pending task can be dropped straight onto today's Dashplan, or
 * assigned a location (picked from a persistent, user-grown list, or typed
 * freeform — see `TaskEditDialog`) so the list can be filtered down to what
 * can be done from wherever you currently are.
 */
import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { Plus, Trash2, CalendarClock, Flag, Pencil, MapPin } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useWorkMode } from "@/hooks/use-work-mode";
import { cn } from "@/lib/utils";
import { todayKey, formatDayLabel, minuteOfDay } from "@/lib/dates";

type Priority = "high" | "medium" | "low";

const PRIORITY_STYLES: Record<Priority, string> = {
  high: "text-destructive",
  medium: "text-amber-400",
  low: "text-muted-foreground",
};

interface LocationRef {
  _id: Id<"locations">;
  name: string;
}

interface EditableTask {
  _id: Id<"tasks">;
  title: string;
  description?: string;
  priority: Priority;
  locationId?: Id<"locations">;
  location: LocationRef | null;
}

/** Sentinels for the location `Select` — Radix rejects an empty string. */
const NO_LOCATION = "__none__";
const NEW_LOCATION = "__new__";

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "text-xs px-2 py-1 rounded border transition-colors",
        active
          ? "border-primary bg-primary/10 text-foreground"
          : "border-border text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
    </button>
  );
}

function TaskEditDialog({
  open,
  onOpenChange,
  task,
  locations,
  onSave,
  onDelete,
  onEnsureLocation,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: EditableTask;
  locations: LocationRef[];
  onSave: (values: {
    title: string;
    description: string;
    priority: Priority;
    locationId: Id<"locations"> | null;
  }) => Promise<void>;
  onDelete: () => Promise<void>;
  onEnsureLocation: (name: string) => Promise<Id<"locations">>;
}) {
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? "");
  const [priority, setPriority] = useState<Priority>(task.priority);
  const [locationChoice, setLocationChoice] = useState<string>(
    task.locationId ?? NO_LOCATION,
  );
  const [newLocationName, setNewLocationName] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim()) return;

    setSaving(true);
    try {
      let locationId: Id<"locations"> | null = null;
      if (locationChoice === NEW_LOCATION) {
        if (newLocationName.trim()) {
          locationId = await onEnsureLocation(newLocationName.trim());
        }
      } else if (locationChoice !== NO_LOCATION) {
        locationId = locationChoice as Id<"locations">;
      }

      await onSave({ title: title.trim(), description, priority, locationId });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit task</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="task-title">Title</Label>
            <Input
              id="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              autoFocus
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="task-description">Description (optional)</Label>
            <Textarea
              id="task-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="task-priority">Priority</Label>
              <Select
                value={priority}
                onValueChange={(value) => setPriority(value as Priority)}
              >
                <SelectTrigger id="task-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="task-location">Location</Label>
              <Select value={locationChoice} onValueChange={setLocationChoice}>
                <SelectTrigger id="task-location">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_LOCATION}>No location</SelectItem>
                  {locations.map((location) => (
                    <SelectItem key={location._id} value={location._id}>
                      {location.name}
                    </SelectItem>
                  ))}
                  <SelectItem value={NEW_LOCATION}>+ New location…</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {locationChoice === NEW_LOCATION && (
            <div className="space-y-1.5">
              <Label htmlFor="task-new-location">New location name</Label>
              <Input
                id="task-new-location"
                value={newLocationName}
                onChange={(e) => setNewLocationName(e.target.value)}
                placeholder="Hytten, N183, …"
                maxLength={50}
                autoFocus
              />
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              className="mr-auto text-destructive hover:text-destructive"
              onClick={async () => {
                await onDelete();
                onOpenChange(false);
              }}
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              Delete
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !title.trim()}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function DailiesPage() {
  const { workMode } = useWorkMode();
  const { toast } = useToast();

  const [activeLocationId, setActiveLocationId] = useState<Id<"locations"> | null>(
    null,
  );

  const pending = useQuery(api.tasks.list, {
    workMode,
    status: "pending",
    ...(activeLocationId ? { locationId: activeLocationId } : {}),
  });
  const log = useQuery(api.tasks.completedLog, { limitDays: 14 });
  const locations = useQuery(api.locations.list, {});

  const createTask = useMutation(api.tasks.create);
  const updateTask = useMutation(api.tasks.update);
  const toggleTask = useMutation(api.tasks.toggle);
  const removeTask = useMutation(api.tasks.remove);
  const scheduleTask = useMutation(api.dashplan.scheduleTask);
  const ensureLocation = useMutation(api.locations.ensureLocation);

  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState<Priority>("medium");
  const [editingTaskId, setEditingTaskId] = useState<Id<"tasks"> | null>(null);

  const add = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) return;

    setTitle("");
    try {
      await createTask({ title: trimmed, priority, workMode });
    } catch (error) {
      setTitle(trimmed); // Put it back so nothing typed is lost.
      toast({
        title: "Could not add task",
        description: String(error),
        variant: "destructive",
      });
    }
  };

  const schedule = async (taskId: Id<"tasks">) => {
    // Drop it at the next half hour, which is almost always what you want.
    const startMinute = Math.min(1410, Math.ceil(minuteOfDay() / 30) * 30);
    await scheduleTask({ taskId, date: todayKey(), startMinute });
    toast({
      title: "Added to today's Dashplan",
      description: "Open Dashplan to move it where it belongs.",
    });
  };

  const editingTask = pending?.find((t) => t._id === editingTaskId) ?? null;

  return (
    <div className="space-y-5 max-w-3xl">
      <h1 className="page-title text-2xl font-semibold">Dailies</h1>

      <form onSubmit={add} className="flex gap-2">
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="What needs doing?"
          maxLength={200}
          aria-label="New task"
        />
        <Select
          value={priority}
          onValueChange={(value) => setPriority(value as Priority)}
        >
          <SelectTrigger className="w-[110px] shrink-0" aria-label="Priority">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" disabled={!title.trim()}>
          <Plus className="w-4 h-4" />
          <span className="sr-only sm:not-sr-only sm:ml-1.5">Add</span>
        </Button>
      </form>

      {locations && locations.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-1.5"
          role="group"
          aria-label="Filter by location"
        >
          <span className="text-xs text-muted-foreground mr-1">Location</span>
          <Chip
            label="All"
            active={activeLocationId === null}
            onClick={() => setActiveLocationId(null)}
          />
          {locations.map((location) => (
            <Chip
              key={location._id}
              label={location.name}
              active={activeLocationId === location._id}
              onClick={() =>
                setActiveLocationId(
                  activeLocationId === location._id ? null : location._id,
                )
              }
            />
          ))}
        </div>
      )}

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending">
            To do {pending && pending.length > 0 && `(${pending.length})`}
          </TabsTrigger>
          <TabsTrigger value="done">Completed</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-2 mt-4">
          {pending === undefined && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
          {pending?.length === 0 && (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                {activeLocationId
                  ? "Nothing pending here."
                  : `Nothing pending in ${workMode} mode. Nice.`}
              </CardContent>
            </Card>
          )}
          {pending?.map((task) => (
            <div
              key={task._id}
              className="group flex items-center gap-3 rounded-lg border border-border bg-card p-3"
            >
              <Checkbox
                checked={false}
                onCheckedChange={() => toggleTask({ id: task._id })}
                aria-label={`Complete ${task.title}`}
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm truncate">{task.title}</p>
                {(task.description || task.location) && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {task.description && (
                      <span className="truncate">{task.description}</span>
                    )}
                    {task.location && (
                      <span className="inline-flex items-center gap-0.5 shrink-0">
                        <MapPin className="w-3 h-3" />
                        {task.location.name}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <Flag
                className={cn(
                  "w-3.5 h-3.5 shrink-0",
                  PRIORITY_STYLES[task.priority],
                )}
                aria-label={`${task.priority} priority`}
              />

              <Button
                variant="ghost"
                size="icon"
                aria-label={`Edit ${task.title}`}
                onClick={() => setEditingTaskId(task._id)}
              >
                <Pencil className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                data-offline-disable
                aria-label={`Schedule ${task.title} on today's Dashplan`}
                onClick={() => schedule(task._id)}
              >
                <CalendarClock className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Delete ${task.title}`}
                className="text-destructive hover:text-destructive"
                onClick={() => removeTask({ id: task._id })}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="done" className="space-y-4 mt-4">
          {log === undefined && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
          {log?.length === 0 && (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                Nothing completed yet.
              </CardContent>
            </Card>
          )}
          {log?.map((group) => (
            <div key={group.date} className="space-y-2">
              <h2 className="text-sm font-medium text-muted-foreground">
                {group.date === "unknown"
                  ? "Date not recorded"
                  : formatDayLabel(group.date)}
              </h2>
              {group.tasks.map((task) => (
                <div
                  key={task._id}
                  className="flex items-center gap-3 rounded-lg border border-border bg-card/50 p-3"
                >
                  <Checkbox
                    checked
                    onCheckedChange={() => toggleTask({ id: task._id })}
                    aria-label={`Reopen ${task.title}`}
                  />
                  <p className="text-sm line-through text-muted-foreground truncate flex-1">
                    {task.title}
                  </p>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${task.title}`}
                    className="text-destructive hover:text-destructive"
                    onClick={() => removeTask({ id: task._id })}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ))}
        </TabsContent>
      </Tabs>

      {editingTask && (
        <TaskEditDialog
          open={editingTaskId !== null}
          onOpenChange={(open) => {
            if (!open) setEditingTaskId(null);
          }}
          task={editingTask}
          locations={locations ?? []}
          onSave={async (values) => {
            try {
              await updateTask({
                id: editingTask._id,
                title: values.title,
                description: values.description,
                priority: values.priority,
                locationId: values.locationId,
              });
            } catch (error) {
              toast({
                title: "Could not save task",
                description: String(error),
                variant: "destructive",
              });
              throw error;
            }
          }}
          onDelete={async () => {
            await removeTask({ id: editingTask._id });
          }}
          onEnsureLocation={(name) => ensureLocation({ name })}
        />
      )}
    </div>
  );
}
